/**
 * Reads back the `ScribeDraft` document the middleware writes at the end of
 * `/generate-draft`. That artifact exists so a client which never received the
 * response — a closed tab, a dropped connection — can still recover the note.
 */
import type { MedplumClient } from '@medplum/core'
import type { GenerateDraftResponse } from '@behuman/core-contract'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { toFinalizeResult, type FinalizeScribeResult } from './useFinalizeScribe'

/**
 * Fetch and decode a stored draft.
 *
 * The attachment is a pre-signed storage URL, so it must go through
 * `downloadAttachment` — `medplum.download()` only resolves `Binary/…`
 * references and fails on these.
 */
export async function loadStoredScribeDraft(
  medplum: MedplumClient,
  docRefId: string,
): Promise<FinalizeScribeResult> {
  const docRef = await medplum.readResource('DocumentReference', docRefId)
  const url = docRef.content?.[0]?.attachment?.url
  if (!url) {
    throw new Error(`Stored scribe draft ${docRefId} has no attachment to read`)
  }
  const blob = await downloadAttachment(medplum, url)
  const parsed = JSON.parse(await blob.text()) as GenerateDraftResponse
  return toFinalizeResult(parsed)
}
